// Smart Daara - Login Page
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { BookOpen, Mail, Lock, Eye, EyeOff, Globe, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { getDashboardPath } from '../lib/routing';

// Smart Daara Logo
const LOGO_URL = '/logo.png';

export default function LoginPage() {
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(language === 'fr' ? 'Connexion réussie!' : 'Login successful!');
      navigate(getDashboardPath(user));
    } catch (error) {
      const message = error.response?.data?.detail || 
        (language === 'fr' ? 'Erreur de connexion' : 'Login failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-amber-50/30 flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <Link to="/home" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-5 h-5" />
          <span>{language === 'fr' ? 'Accueil' : 'Home'}</span>
        </Link>
        <Button variant="ghost" size="sm" onClick={toggleLanguage} className="flex items-center gap-1">
          <Globe className="w-4 h-4" />
          {language.toUpperCase()}
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-6">
              <img src={LOGO_URL} alt="Smart Daara" className="h-48 w-auto" />
            </div>
            <CardTitle className="text-2xl font-outfit">{t('loginTitle')}</CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? 'Entrez vos identifiants pour accéder à votre compte'
                : 'Enter your credentials to access your account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-sm text-emerald-700 hover:underline"
                  onClick={() => toast.info(language === 'fr'
                    ? "Contactez l'administrateur de votre école pour réinitialiser votre mot de passe."
                    : 'Contact your school administrator to reset your password.')}
                  data-testid="forgot-password-btn"
                >
                  {t('forgotPassword')}
                </button>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('loading')}
                  </span>
                ) : (
                  t('login')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500">
                {language === 'fr'
                  ? 'Vous n\'avez pas de compte ? Contactez votre administrateur d\'école pour en créer un.'
                  : 'No account? Contact your school administrator to have one created.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
